import React, { useCallback, useMemo, useState } from 'react';
import { SurveyFilter, SurveyTable } from '../components';
import { useSearchSurveys } from '../hooks';
import { useObservable } from '@lib/common/utils/useObservable';
import {
  guestSurveyService,
  sessionQuery,
  SurveyOutreachFilters,
  UpdateGuestSurveyOutreachRequest,
} from '@lib/state';
import { Section, usePageTitle } from '@lib/common';
import { useLocation } from 'react-router-dom';
import { AuditLogs } from 'app/shared';

export function SearchSurveysPage() {
  const queryParams = new URLSearchParams(useLocation().search);
  const recordParam = queryParams.get('reservation');
  const outreachCompletedParam = queryParams.get('outreachCompleted');
  const recordNumber = useMemo(() => {
    if (!!recordParam) {
      const parsed = Number(recordParam);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return undefined;
  }, [recordParam]);
  const outreachCompleted = useMemo(() => {
    if (!!outreachCompletedParam) {
      return outreachCompletedParam.toLowerCase() === 'true';
    }

    return undefined;
  }, [outreachCompletedParam]);

  const propertyId = useObservable(sessionQuery.propertyId);
  const [guestSurveyFilters, setGuestSurveyFilters] = useState<SurveyOutreachFilters>({
    recordNumber,
    outreachCompleted,
  });
  const { surveys, pagination, loading } = useSearchSurveys(propertyId, guestSurveyFilters);

  usePageTitle('Survey Outreach');

  const onEditSubmit = useCallback(
    (survey: UpdateGuestSurveyOutreachRequest) => {
      if (propertyId && survey.guestSurveyId) {
        guestSurveyService.submitOutreach(propertyId, survey);
      }
    },
    [propertyId]
  );

  const onShowMore = useCallback(() => {
    if (propertyId && !pagination.isDone && pagination.continuationToken) {
      guestSurveyService.getGuestSurveys(
        propertyId,
        guestSurveyFilters,
        pagination.continuationToken
      );
    }
  }, [propertyId, guestSurveyFilters, pagination]);

  return (
    <Section>
      <SurveyFilter filters={guestSurveyFilters} onChange={setGuestSurveyFilters} />
      <SurveyTable
        surveys={surveys}
        loading={loading}
        onEditSubmit={onEditSubmit}
        isDone={pagination.isDone}
        onShowMore={onShowMore}
      />
      <Section title="Survey History">
        <AuditLogs scope="guest-surveys" id={propertyId ?? undefined} />
      </Section>
    </Section>
  );
}
